import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { useFormikContext } from 'formik'
import { Box, IconDelete, Skeleton, Text } from '@machobear/design-system'
import { show as showVideoLightBox } from '@/components/video-light-box'
import { colors } from '@/styles'
import { checkVideoLink } from '../util'
import VideoThumbnail from '../components/video-thumbnail'

interface Props {
  label?: string
  listingId: string
  unitId: string
  videoData: any
  isUploading: boolean
  setIsUploading: (value: boolean) => void
  [key: string]: any
}

const UploadVideoPreview = ({
  label,
  listingId,
  unitId,
  videoData,
  isUploading,
  setIsUploading,
  ...rest
}: Props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { setFieldValue } = useFormikContext()
  const location = videoData?.[0]?.location?.[1]

  // a time gap exists between when video upload is completed (SSE) and when the
  // video actually works (CDN propagation time). This useQuery retries loading
  // the video in the background until it succeeds.
  const { isLoading, isError } = useQuery({
    queryKey: ['videoLinks', location?.key],
    queryFn: () => checkVideoLink(location?.key),
    staleTime: Infinity,
    gcTime: Infinity,
    retryDelay: 1000,
    retry: 300,
    enabled: !!location?.key // prevents useQuery from running when the videoData is undefined
  })

  useEffect(() => {
    if (isUploading && !isLoading) {
      setIsUploading(false)
    }
  }, [isUploading, isLoading, setIsUploading])

  if (isError) {
    // shouldn't hit
    return (
      <Box>
        <Text size="small" weight="medium">
          Video url is invalid
        </Text>
      </Box>
    )
  }

  if (!location?.key) {
    // shouldn't hit
    return null
  }

  const onDelete = async () => {
    try {
      dispatch.listing.deleteUnitFile({
        listingId,
        unitId,
        fileId: videoData?.[0]?.id
      })
    } catch {
      // error
    }
    setFieldValue('videos', [])
  }

  const handleClickThumbnail = () => {
    showVideoLightBox({
      videoLocation: videoData?.[0]?.location,
      controls: true,
      autoPlay: true
    })
  }

  return (
    <>
      <Box
        position="relative"
        display="flex"
        justifyContent="spaceBetween"
        border="borderNeutralLight"
        borderRadius="small"
        width="full"
        paddingY="xxsmall"
        paddingX="small"
        {...rest}
      >
        {isLoading ? (
          <Box display="flex" justifyContent="flexStart" alignItems="center" gap="small">
            <Skeleton.Shape height={50} width={65} />
            {isUploading && (
              <Text size="small" weight="medium">
                {t('Video processing...')}
              </Text>
            )}
          </Box>
        ) : (
          <Box width="full" display="flex" alignItems="center" gap="small">
            <VideoThumbnail videoType="2d" handleClickThumbnail={handleClickThumbnail}>
              <video
                autoPlay={false}
                controls={false}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center'
                }}
              >
                <source key={location.key} src={location.key} type={location.type} />
              </video>
            </VideoThumbnail>
            <Box
              width="full"
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="spaceBetween"
              gap="small"
              style={{ minWidth: '0' }}
            >
              {label && (
                <Box width="full" style={{ minWidth: '0' }}>
                  <Text size="small" weight="medium" maxLines={1}>
                    {label}
                  </Text>
                </Box>
              )}
            </Box>
            <Box display="flex" justifyContent="center" alignItems="center" cursor="pointer">
              <Box
                padding="xxxsmall"
                style={{
                  backgroundColor: colors.backgroundCriticalSoft,
                  borderRadius: '50%'
                }}
                onClick={onDelete}
              >
                <IconDelete size="xsmall" tone="brandAccent" />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </>
  )
}

export default UploadVideoPreview
